/**
 * Step "Kerntaken" — Epic 4 (Beoordelen op kerntaken), Story 4.2 + 4.3
 *
 * Vervanger van Step2Tasks voor `core_tasks_v2`-beoordelingen (AD-4).
 *
 * S4 (Story 4.2, precies 1 gedekt tijdvak): maximaal 3 (bij uitzondering
 * 4-5) kerntaak-kaarten zonder accordeons, vaardigheden als ankers (FR-9),
 * live punteneffect via de gedeelde scoringmodule.
 *
 * S5 (Story 4.3, >=2 gedekte tijdvakken): uitbreiding met een periodebalk,
 * kerntaak-kaarten gegroepeerd per tijdvak, en een uitgesplitste
 * totaalregel ("0,25 x 38 + 0,75 x 44 = 42,5 / 50", AC #2). Welke laag
 * rendert wordt uitsluitend bepaald door `resolvePeriodsAndWeights` uit de
 * gedeelde module (AD-3/AD-6) — geen eigen tijdvak-telling.
 */

import { useState, useEffect, useMemo } from 'react';
import { useEvaluation, useSaveCoreTaskScores } from '../useEvaluations';
import { useTaskPeriods } from '../../employees/useAssignmentPeriods';
import { useCoreTasks } from '../../settings/useCoreTasks';
import { useWizard } from './WizardContext';
import { KerntaakScoreCard } from './KerntaakScoreCard';
import { PeriodBar } from './PeriodBar';
import { PACKAGE_LETTERS } from './periodLabels';
import { PackageSnapshotView } from '../PackageSnapshotView';
import {
  resolvePeriodsAndWeights,
  calculateWeightedCoreTaskBlock,
  roundTo1Decimal,
  type CoreTaskAssignmentScore,
  type PackageSnapshot,
} from '@functieloonhuis/shared';
import type { CoreTaskScore } from '../../../lib/evaluationsApi';
import type { AssignmentPeriodDTO } from '@functieloonhuis/shared';

interface StepKerntakenProps {
  evaluationId: number;
}

interface LocalCoreTaskScore {
  periodId: number;
  coreTaskId: number;
  score: number | null;
  toelichting: string;
}

const MONTH_NAMES_NL = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
];

/** "31 maart" uit een ISO-datum (Nederlandse weergave, dag-maand). */
function formatDateNl(isoDate: string): string {
  const day = Number(isoDate.slice(8, 10));
  const month = Number(isoDate.slice(5, 7));
  return `${day} ${MONTH_NAMES_NL[month - 1] ?? ''}`;
}

/** Getal met komma als decimaalteken, `decimals` decimalen (NL-notatie voor de uitsplitsing). */
function formatNl(value: number, decimals: number): string {
  return value.toFixed(decimals).replace('.', ',');
}

interface TotalLineProps {
  isMultiPeriod: boolean;
  weightedResult: ReturnType<typeof calculateWeightedCoreTaskBlock> | null;
}

/** Totaalregel — enkelvoudig (S4) of uitgesplitst per tijdvak (S5, AC #2). */
function TotalLine({ isMultiPeriod, weightedResult }: TotalLineProps) {
  if (!weightedResult) return null;

  const totalLabel = roundTo1Decimal(weightedResult.totalPoints).toFixed(1);

  if (!isMultiPeriod) {
    return (
      <div className="bg-white p-4 rounded-lg border border-[#E2E8F0] flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Taken-blok</span>
        <span className="text-lg font-bold text-[#1E293B]">
          {totalLabel} <span className="text-sm font-normal text-gray-500">/ 50</span>
        </span>
      </div>
    );
  }

  // AD-3 afrondingsconventie: de getoonde termen (gewicht, per-tijdvak-blok)
  // worden afgerond op weergaveniveau; het totaal komt uit de
  // volle-precisie-som (`weightedResult.totalPoints`) — een schijnbare
  // optel-afwijking van <=0,1 tussen de som van de getoonde termen en het
  // totaal is per definitie geen fout.
  const equation = weightedResult.periodBreakdown
    .map((p) => `${formatNl(p.weight, 2)} × ${formatNl(roundTo1Decimal(p.blockPoints), 1)}`)
    .join(' + ');

  return (
    <div data-testid="core-total-line-split" className="bg-white p-4 rounded-lg border border-[#E2E8F0] space-y-1">
      <span className="text-sm font-medium text-gray-700">Taken-blok</span>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">
          {equation} = {formatNl(Number(totalLabel), 1)}
        </span>
        <span className="text-lg font-bold text-[#1E293B]">
          {totalLabel} <span className="text-sm font-normal text-gray-500">/ 50</span>
        </span>
      </div>
    </div>
  );
}

export function StepKerntaken({ evaluationId }: StepKerntakenProps) {
  const { data: evaluation, isLoading: isLoadingEval } = useEvaluation(evaluationId);

  // Epic 4 (Story 4.4, AC #3, AD-5): een afgeronde (`final`) beoordeling met
  // een package_snapshot toont UITSLUITEND dat snapshot — nooit een live
  // join met assignment_periods/core_tasks (dat zou latere catalogus-
  // hernoemingen of pakketwijzigingen laten "doorlekken" in een al
  // afgeronde beoordeling). `useTaskPeriods` wordt in dat geval niet eens
  // met een employeeId aangeroepen (geen onnodige live-fetch).
  const isFinalSnapshot = evaluation?.status === 'final' && Boolean(evaluation?.packageSnapshot);
  const snapshot: PackageSnapshot | null = useMemo(() => {
    if (!evaluation?.packageSnapshot) return null;
    try {
      return JSON.parse(evaluation.packageSnapshot) as PackageSnapshot;
    } catch {
      return null;
    }
  }, [evaluation?.packageSnapshot]);

  const { data: allPeriods, isLoading: isLoadingPeriods } = useTaskPeriods(
    isFinalSnapshot ? undefined : evaluation?.employeeId
  );
  const { data: catalog, isLoading: isLoadingCatalog } = useCoreTasks();
  const saveCoreTaskScores = useSaveCoreTaskScores();
  const { stepData, updateStepData, prevStep, nextStep } = useWizard();

  const [localScores, setLocalScores] = useState<LocalCoreTaskScore[]>([]);

  const evaluationWindow = useMemo(
    () => (evaluation ? { start: evaluation.periodeStart, end: evaluation.periodeEnd } : null),
    [evaluation]
  );

  const resolvedWeights = useMemo(() => {
    if (!evaluationWindow || !allPeriods) return null;
    return resolvePeriodsAndWeights(
      evaluationWindow,
      allPeriods.map((p) => ({ id: p.id, startDate: p.startDate, endDate: p.endDate }))
    );
  }, [evaluationWindow, allPeriods]);

  // Task 1 (AC #3): uitsluitend tijdvakken die daadwerkelijk overlappen met
  // het beoordelingsvenster (AD-6) — geen eigen tijdvak-telling, dit komt
  // 1-op-1 uit `resolvePeriodsAndWeights`.
  const coveredPeriods: AssignmentPeriodDTO[] = useMemo(() => {
    if (!resolvedWeights || !allPeriods) return [];
    const coveredIds = new Set(resolvedWeights.weights.map((w) => w.periodId));
    return allPeriods.filter((p) => coveredIds.has(p.id)).sort((a, b) => a.startDate.localeCompare(b.startDate));
  }, [resolvedWeights, allPeriods]);

  // AC #3: bij precies 1 gedekt tijdvak blijft de eenvoudige S4-weergave
  // (Story 4.2) ongewijzigd — géén periodebalk, géén groepering.
  const isMultiPeriod = coveredPeriods.length >= 2;

  useEffect(() => {
    if (coveredPeriods.length === 0) return;

    const existingScores = stepData.step2?.coreTaskScores || [];
    const initial: LocalCoreTaskScore[] = [];
    for (const period of coveredPeriods) {
      for (const assignment of period.assignments) {
        const existing = existingScores.find(
          (s) => s.periodId === period.id && s.coreTaskId === assignment.coreTaskId
        );
        initial.push({
          periodId: period.id,
          coreTaskId: assignment.coreTaskId,
          score: existing?.score ?? null,
          toelichting: existing?.toelichting || '',
        });
      }
    }
    setLocalScores(initial);
  }, [coveredPeriods, stepData.step2?.coreTaskScores]);

  const skillsByCoreTaskId = useMemo(() => {
    const map = new Map<number, string[]>();
    catalog?.forEach((task) => {
      map.set(task.id, task.skills.map((s) => s.label));
    });
    return map;
  }, [catalog]);

  // Live punteneffect — uitsluitend via de gedeelde module (AD-3), geen eigen berekening
  const scoresPerPeriod = useMemo(() => {
    const map: Record<number, CoreTaskAssignmentScore[]> = {};
    for (const period of coveredPeriods) {
      map[period.id] = period.assignments.map((a) => {
        const local = localScores.find((ls) => ls.periodId === period.id && ls.coreTaskId === a.coreTaskId);
        return { coreTaskId: a.coreTaskId, timePercentage: a.timePercentage, score: local?.score ?? null };
      });
    }
    return map;
  }, [coveredPeriods, localScores]);

  const weightedResult = useMemo(() => {
    if (!evaluationWindow || coveredPeriods.length === 0) return null;
    return calculateWeightedCoreTaskBlock(
      coveredPeriods.map((p) => ({ id: p.id, startDate: p.startDate, endDate: p.endDate })),
      scoresPerPeriod,
      evaluationWindow
    );
  }, [coveredPeriods, scoresPerPeriod, evaluationWindow]);

  const handleScoreChange = (periodId: number, coreTaskId: number, score: number) => {
    setLocalScores((prev) =>
      prev.map((ls) => (ls.periodId === periodId && ls.coreTaskId === coreTaskId ? { ...ls, score } : ls))
    );
  };

  const handleToelichtingChange = (periodId: number, coreTaskId: number, toelichting: string) => {
    setLocalScores((prev) =>
      prev.map((ls) => (ls.periodId === periodId && ls.coreTaskId === coreTaskId ? { ...ls, toelichting } : ls))
    );
  };

  const handleSave = async () => {
    if (coveredPeriods.length === 0) return;

    // Task 3 (AD-6): scores per tijdvak apart — elke entry draagt zijn
    // eigen `periodId` (core_task_scores.period_id NOT NULL). Eén
    // gecombineerde save-call volstaat: de backend upsert per
    // (evaluationId, periodId, coreTaskId).
    const validScores: CoreTaskScore[] = localScores
      .filter((ls) => ls.score !== null)
      .map((ls) => ({
        periodId: ls.periodId,
        coreTaskId: ls.coreTaskId,
        score: ls.score!,
        toelichting: ls.toelichting || undefined,
      }));

    updateStepData('step2', {
      taskScores: stepData.step2?.taskScores ?? [],
      coreTaskScores: validScores,
      totalCoreTasks: localScores.length,
    });

    if (validScores.length > 0) {
      await saveCoreTaskScores.mutateAsync({ evaluationId, scores: validScores });
    }
  };

  const handleNext = async () => {
    await handleSave();
    nextStep();
  };

  if (isFinalSnapshot) {
    if (isLoadingEval) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2F5A7A]"></div>
          <span className="ml-3 text-gray-600">Kerntaken laden...</span>
        </div>
      );
    }
    if (!snapshot) {
      return null;
    }
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-[#1E293B]">Kerntaken</h2>
          <p className="mt-1 text-sm text-gray-500">
            Deze beoordeling is definitief — het beoordeelde pakket wordt vastgehouden zoals het was op het moment van afronden.
          </p>
        </div>
        <PackageSnapshotView snapshot={snapshot} finalizedAt={evaluation?.finalizedAt ?? null} />
        <div className="flex items-center justify-between pt-4 border-t border-[#E2E8F0]">
          <button
            onClick={prevStep}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Vorige
          </button>
          <button
            onClick={nextStep}
            className="px-6 py-2 bg-[#2F5A7A] text-white rounded-lg hover:bg-[#2F5A7A]/90 transition-colors"
          >
            Volgende: Vakcompetenties
          </button>
        </div>
      </div>
    );
  }

  const isLoading = isLoadingEval || isLoadingPeriods || isLoadingCatalog;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2F5A7A]"></div>
        <span className="ml-3 text-gray-600">Kerntaken laden...</span>
      </div>
    );
  }

  const singlePeriod = coveredPeriods[0];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#1E293B]">Kerntaken Beoordelen</h2>
        <p className="mt-1 text-sm text-gray-500">
          Beoordeel elke kerntaak op een schaal van 1-5. De eindscore wordt tijdgewogen berekend.
        </p>
      </div>

      {/* Periodebalk — uitsluitend bij >=2 gedekte tijdvakken (AC #1/#3) */}
      {isMultiPeriod && resolvedWeights && (
        <PeriodBar
          segments={resolvedWeights.weights.map((w) => {
            const period = coveredPeriods.find((p) => p.id === w.periodId)!;
            return { periodId: w.periodId, startDate: period.startDate, endDate: period.endDate, months: w.months };
          })}
          totalCoveredMonths={resolvedWeights.totalCoveredMonths}
        />
      )}

      {/* Kerntaak-kaarten — géén accordeons, alles direct zichtbaar (NFR-2/PRD §10) */}
      {isMultiPeriod ? (
        <div data-testid="kerntaken-cards-grouped" className="space-y-6">
          {coveredPeriods.map((period, index) => {
            const weightEntry = resolvedWeights?.weights.find((w) => w.periodId === period.id);
            const weightPct = weightEntry ? roundTo1Decimal(weightEntry.weight * 100) : 0;
            const endLabel = period.endDate ? `t/m ${formatDateNl(period.endDate)}` : '(lopend)';

            return (
              <div key={period.id} data-testid={`period-group-${period.id}`} className="space-y-3">
                <h3 className="text-sm font-semibold text-[#1E293B]">
                  Pakket {PACKAGE_LETTERS[index % PACKAGE_LETTERS.length]} {endLabel} &mdash; telt voor {weightPct}%
                </h3>
                <div className="space-y-4">
                  {period.assignments.map((assignment) => {
                    const localScore = localScores.find(
                      (ls) => ls.periodId === period.id && ls.coreTaskId === assignment.coreTaskId
                    );
                    const skills = skillsByCoreTaskId.get(assignment.coreTaskId) ?? [];
                    const earnedWithinPeriod =
                      localScore?.score != null ? 50 * (assignment.timePercentage / 100) * (localScore.score / 5) : 0;

                    return (
                      <KerntaakScoreCard
                        key={assignment.coreTaskId}
                        coreTaskId={assignment.coreTaskId}
                        coreTaskName={assignment.coreTaskName}
                        timePercentage={assignment.timePercentage}
                        skills={skills}
                        score={localScore?.score ?? null}
                        toelichting={localScore?.toelichting || ''}
                        earnedPointsWithinPeriod={earnedWithinPeriod}
                        onScoreChange={(score) => handleScoreChange(period.id, assignment.coreTaskId, score)}
                        onToelichtingChange={(t) => handleToelichtingChange(period.id, assignment.coreTaskId, t)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div data-testid="kerntaken-cards" className="space-y-4">
          {singlePeriod?.assignments.map((assignment) => {
            const localScore = localScores.find(
              (ls) => ls.periodId === singlePeriod.id && ls.coreTaskId === assignment.coreTaskId
            );
            const skills = skillsByCoreTaskId.get(assignment.coreTaskId) ?? [];
            const earnedPoints =
              localScore?.score != null ? 50 * (assignment.timePercentage / 100) * (localScore.score / 5) : 0;

            return (
              <KerntaakScoreCard
                key={assignment.coreTaskId}
                coreTaskId={assignment.coreTaskId}
                coreTaskName={assignment.coreTaskName}
                timePercentage={assignment.timePercentage}
                skills={skills}
                score={localScore?.score ?? null}
                toelichting={localScore?.toelichting || ''}
                earnedPointsWithinPeriod={earnedPoints}
                onScoreChange={(score) => handleScoreChange(singlePeriod.id, assignment.coreTaskId, score)}
                onToelichtingChange={(t) => handleToelichtingChange(singlePeriod.id, assignment.coreTaskId, t)}
              />
            );
          })}
        </div>
      )}

      {/* Totaalregel — zelfde patroon als Step5Review; uitgesplitst bij >=2 tijdvakken (AC #2) */}
      <TotalLine isMultiPeriod={isMultiPeriod} weightedResult={weightedResult} />

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-[#E2E8F0]">
        <button
          onClick={prevStep}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Vorige
        </button>
        <div className="flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saveCoreTaskScores.isPending}
            className="px-4 py-2 text-[#2F5A7A] border border-[#2F5A7A] rounded-lg hover:bg-[#2F5A7A]/5 disabled:opacity-50"
          >
            {saveCoreTaskScores.isPending ? 'Opslaan...' : 'Opslaan'}
          </button>
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-[#2F5A7A] text-white rounded-lg hover:bg-[#2F5A7A]/90 transition-colors"
          >
            Volgende: Vakcompetenties
          </button>
        </div>
      </div>
    </div>
  );
}
